import { v4 as uuidv4 } from "uuid";
import { FilterValues } from "../../../data-model/filterValues";
import { mergeSearchFiltersWithSidebarFilters } from "./buildOncologySubmissionSearchFilters";
import {
  OncologySubmissionFilterPayload,
  ONCOLOGY_SUBMISSION_DEFAULT_ORDER_BY,
} from "./oncologySubmissionFilterPayload";
import {
  convertDefaultDateToDumpedAtFormat,
  processFilterData,
} from "./processFilterData";

/**
 * Builds the oncology submission table API payload for filter and search requests.
 *
 * @input validFilters - Active sidebar filters for this table
 * @input searchTerm - Current search text (empty when not searching)
 * @input dateFromStart - Default range start date (YYYYMMDD)
 * @input dateFromEnd - Default range end date (YYYYMMDD)
 * @input pageSize - Number of rows per page
 * @input page - Page number to request (1-indexed)
 * @return OncologySubmissionFilterPayload: Request body for the BigQuery API
 */
export const buildOncologySubmissionFilterPayload = (
  validFilters: FilterValues[],
  searchTerm: string,
  dateFromStart: string,
  dateFromEnd: string,
  pageSize: number,
  page: number = 1,
): OncologySubmissionFilterPayload => {
  const { filters: apiFilters } = processFilterData(validFilters);

  const hasSearchTerm = (searchTerm ?? "").trim().length > 0;
  const hasFilters = (validFilters?.length ?? 0) > 0;

  // Only omit dates when searching and no filter is applied (backend searches all dates).
  // When any filter is applied, keep dates and still send search term if present.
  const omitDates = hasSearchTerm && !hasFilters;
  if (!omitDates) {
    if (!apiFilters.dumped_at || apiFilters.dumped_at.length < 2) {
      apiFilters.dumped_at = [
        convertDefaultDateToDumpedAtFormat(dateFromStart),
        convertDefaultDateToDumpedAtFormat(dateFromEnd),
      ];
    }
  }

  if (hasSearchTerm) {
    return {
      case_sensitive: false,
      filters: mergeSearchFiltersWithSidebarFilters(searchTerm, apiFilters),
      page,
      page_size: pageSize,
      request_id: uuidv4(),
      order_by: ONCOLOGY_SUBMISSION_DEFAULT_ORDER_BY,
    };
  }

  return {
    filters: apiFilters,
    page,
    page_size: pageSize,
    request_id: uuidv4(),
    order_by: ONCOLOGY_SUBMISSION_DEFAULT_ORDER_BY,
  };
};
